import { Switch } from 'components/ui/switch';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations.ts';
import ReadonlyField from '../rhf-other/ReadonlyField.tsx';
import { CommonInputProps } from './InputTypes.tsx';
import InputWrapper from './InputWrapper.tsx';
import { useMemoizedRules } from './useMemoizedRules.ts';

interface Props<T extends FieldValues> extends CommonInputProps<T> {
    onChange?: (value: boolean) => void;
    readonly?: boolean;
}

const SwitchInput = <T extends FieldValues>({
    name,
    control,
    disabled,
    label,
    required,
    shouldTranslateLabel = true,
    wrapperClassName,
    validate,
    onChange,
    readonly = false,
    tooltip,
}: Props<T>) => {
    const rules = useMemoizedRules({ required, validate });

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { ref, value, onBlur, onChange: innerOnChange }, fieldState: { error } }) =>
                readonly ? (
                    <InputWrapper
                        className={wrapperClassName}
                        name={name}
                        label={label}
                        required={required}
                        readonly={readonly}
                        shouldTranslateLabel={shouldTranslateLabel}
                        hasErrors={!!error}
                        tooltip={tooltip}
                    >
                        <ReadonlyField value={value ? t('yes') : t('no')} />
                    </InputWrapper>
                ) : (
                    <div className={cn('flex flex-row items-center gap-1.5', wrapperClassName)}>
                        {/* The switch carries its own 8px margin on every side, so it
                            also sets the row's height and the space after the label and before the tooltip. */}
                        <div className="flex flex-row items-center">
                            {label && (
                                <label
                                    htmlFor={name}
                                    className={cn(
                                        'text-fg-secondary text-sm',
                                        error && 'text-destructive new:text-danger-ink'
                                    )}
                                >
                                    {shouldTranslateLabel ? t(label) : label}
                                    {required ? (
                                        <span className="text-destructive new:text-danger-ink">&thinsp;*</span>
                                    ) : null}
                                </label>
                            )}
                            <Switch
                                id={name}
                                name={name}
                                ref={ref}
                                className="m-2"
                                disabled={disabled}
                                onBlur={onBlur}
                                aria-invalid={!!error}
                                checked={value === undefined || value === null ? false : value}
                                onCheckedChange={(checked) => {
                                    innerOnChange(checked as PathValue<T, Path<T>>);
                                    onChange?.(checked);
                                }}
                            />
                        </div>
                        {tooltip ? tooltip : null}
                    </div>
                )
            }
        />
    );
};

export default SwitchInput;
